import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface UseLiveAnnouncer {
  /** Rendered into the control's one `role="status"` region. Empty while idle. */
  announcement: string;
  say: (text: string) => void;
  clear: () => void;
  /**
   * The refusal a read-only field owes. Silence here is what made the FIRST
   * read-only implementation worse than the `disabled` it replaced: a control
   * that focuses, clicks and does nothing reads as broken.
   */
  announceReadOnlyRefusal: () => void;
}

/**
 * What a kit control says out loud — one implementation for every family.
 *
 * `selection/` and `structured/` each grew their own copy of this, and by the
 * time `scalar/` arrived the two copies differed only in which catalogue key
 * held the same Arabic sentence. A third copy is a third wording waiting to
 * happen, so the state, the memoization and the refusal live here and each
 * family keeps its own hook as a one-line delegate that supplies its key.
 *
 * The key is a parameter rather than the sentence, so the families stay free to
 * word their refusal differently if a control ever needs to — without being
 * able to do it by accident.
 */
export function useLiveAnnouncer(readOnlyRefusalKey: string): UseLiveAnnouncer {
  const { t } = useTranslation('uiKit');
  const [announcement, setAnnouncement] = useState('');

  const say = useCallback((text: string) => {
    setAnnouncement(text);
  }, []);

  const clear = useCallback(() => {
    setAnnouncement('');
  }, []);

  const announceReadOnlyRefusal = useCallback(() => {
    setAnnouncement(t(readOnlyRefusalKey));
  }, [readOnlyRefusalKey, t]);

  // Memoized as a whole: a fresh object every render makes every dependent
  // `useCallback` decorative, which is worse than not having one — it reads as
  // memoized and is not.
  return useMemo(
    () => ({ announcement, say, clear, announceReadOnlyRefusal }),
    [announcement, announceReadOnlyRefusal, clear, say],
  );
}
